import { Table, Input, InputNumber } from "antd";
import { useState, useEffect } from "react";
import {
  Converse,
  useModus,
  ModusProvider,
  MicRecorderProvider
} from "@vojomo-core/shared";

interface TableData {
  columns: any[];
  dataSource: any[];
}

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: string;
  record: any;
  handleSave: (record: any) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(record?.[dataIndex]);

  useEffect(() => {
    setValue(record?.[dataIndex]);
  }, [record, dataIndex]);

  const toggleEdit = () => {
    setEditing(!editing);
  };

  const save = () => {
    toggleEdit();
    handleSave({ ...record, [dataIndex]: value });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      save();
    } else if (e.key === "Escape") {
      setValue(record?.[dataIndex]);
      toggleEdit();
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      typeof value === "number" ? (
        <InputNumber
          value={value}
          onChange={(val) => setValue(val)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          autoFocus
          style={{ width: "100%" }}
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      )
    ) : (
      <div
        onClick={toggleEdit}
        style={{
          minHeight: "22px",
          cursor: "pointer",
          padding: "4px 11px",
          border: "1px solid transparent",
          borderRadius: "2px"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.border = "1px solid #d9d9d9";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.border = "1px solid transparent";
        }}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

export const TableTest = () => {
  return (
    <ModusProvider agentName="TableTestAgent">
      <MicRecorderProvider>
        <TableTestInternal />
      </MicRecorderProvider>
    </ModusProvider>
  );
};

const TableTestInternal = () => {
  const {
    sendMessage,
    fmtLatestMessage,
    isWaitingForResponse,
    latestVoMessage
  } = useModus();
  const [tableData, setTableData] = useState<TableData>({
    columns: [],
    dataSource: []
  });

  // Process the agent's response and extract table data
  useEffect(() => {
    console.log("Effect triggered:", {
      fmtLatestMessage,
      latestVoMessage,
      isWaitingForResponse,
      hasMessage: !!fmtLatestMessage
    });

    if (latestVoMessage && !isWaitingForResponse) {
      console.log("Processing message:", latestVoMessage.content);

      try {
        // Try to parse the message as JSON
        const parsed = JSON.parse(
          latestVoMessage.content.replace("json", "").replaceAll("```", "")
        );

        console.log("Parsed successfully:", parsed);

        if (parsed.columns && parsed.dataSource) {
          console.log("Using format 1: columns + dataSource");
          // Direct format: { columns: [...], dataSource: [...] }
          setTableData({
            columns: parsed.columns,
            dataSource: parsed.dataSource
          });
        } else if (
          parsed.data &&
          Array.isArray(parsed.data) &&
          parsed.data.length > 0
        ) {
          console.log("Using format 2: data array");
          // Infer columns from data keys
          const firstItem = parsed.data[0];
          const columns = Object.keys(firstItem).map((key) => ({
            title: key.charAt(0).toUpperCase() + key.slice(1),
            dataIndex: key,
            key: key,
            editable: true
          }));

          const dataSource = parsed.data.map((item, index) => ({
            ...item,
            key: item.key || item.id || index
          }));

          console.log("Setting table data:", { columns, dataSource });

          setTableData({
            columns,
            dataSource
          });
        } else if (Array.isArray(parsed) && parsed.length > 0) {
          console.log("Using format 3: direct array");
          // Direct array format
          const firstItem = parsed[0];
          const columns = Object.keys(firstItem).map((key) => ({
            title: key.charAt(0).toUpperCase() + key.slice(1),
            dataIndex: key,
            key: key
          }));

          const dataSource = parsed.map((item, index) => ({
            ...item,
            key: item.key || item.id || index
          }));

          console.log("Setting table data:", { columns, dataSource });

          setTableData({
            columns,
            dataSource
          });
        } else {
          console.log("No matching format found for parsed data");
        }
      } catch (e) {
        console.log("Not direct JSON, trying to extract from markdown");
        // If not JSON, try to extract JSON from markdown code blocks
        const jsonMatch = fmtLatestMessage.match(
          /```(?:json)?\s*(\{[\s\S]*?\}|\[[\s\S]*?\])\s*```/
        );
        if (jsonMatch) {
          console.log("Found JSON in markdown block:", jsonMatch[1]);
          try {
            const parsed = JSON.parse(jsonMatch[1]);
            console.log("Parsed from markdown:", parsed);
            if (parsed.columns && parsed.dataSource) {
              console.log("Using format 1 from markdown: columns + dataSource");
              setTableData({
                columns: parsed.columns,
                dataSource: parsed.dataSource
              });
            } else if (Array.isArray(parsed) && parsed.length > 0) {
              console.log("Using format 3 from markdown: direct array");
              const firstItem = parsed[0];
              const columns = Object.keys(firstItem).map((key) => ({
                title: key.charAt(0).toUpperCase() + key.slice(1),
                dataIndex: key,
                key: key
              }));

              const dataSource = parsed.map((item: any, index: number) => ({
                ...item,
                key: item.key || item.id || index
              }));

              console.log("Setting table data from markdown:", {
                columns,
                dataSource
              });

              setTableData({
                columns,
                dataSource
              });
            }
          } catch (parseError) {
            console.error("Failed to parse JSON from code block:", parseError);
          }
        } else {
          console.log("No JSON markdown block found in message");
        }
      }
    } else {
      console.log("Skipping processing - waiting for response or no message");
    }
  }, [fmtLatestMessage, isWaitingForResponse]);

  const handleProcess = async (transcript: string) => {
    await sendMessage(transcript);
  };

  const handleSave = (row: any) => {
    const newData = [...tableData.dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    if (index > -1) {
      newData[index] = row;
      setTableData({ ...tableData, dataSource: newData });
    }
  };

  const editableColumns = tableData.columns.map((col) => {
    if (!col.editable && col.editable !== undefined) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editable: true,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave
      })
    };
  });

  return (
    <div
      style={{
        padding: "24px",
        height: "100vh",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div style={{ marginBottom: "24px" }}>
        <h1>Table Test - Modus Agent Integration</h1>
        <p>
          Ask the agent to generate table data. For example: "Show me a table of
          sample users with name, email, and role"
        </p>
      </div>

      <div style={{ flex: 1, display: "flex", gap: "24px", minHeight: 0 }}>
        <div
          style={{
            width: "400px",
            height: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Converse
            size={150}
            onProcess={handleProcess}
            showTranscriptIcon={false}
            fontSizeOverride="14px"
            orbFontSizeOverride="12px"
            placeHolder="Ask for table data..."
          />
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          {tableData.dataSource.length > 0 ? (
            <Table
              components={{
                body: {
                  cell: EditableCell
                }
              }}
              columns={editableColumns}
              dataSource={tableData.dataSource}
              pagination={{ pageSize: 10 }}
              bordered
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#999"
              }}
            >
              No data yet. Ask the agent to generate a table.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
